import { useState } from "react";
import { Loader2 } from "lucide-react";
import { api, SELLER_TYPES } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { name: "", company_name: "", seller_type: "Individual Seller", mobile: "", whatsapp: "", city: "", state: "", notes: "" };

export function QuickAddSeller({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim() || !form.whatsapp.trim()) return toast.error("Name, mobile and WhatsApp required");
    setSaving(true);
    try {
      const r = await api.post("/admin/sellers", form);
      toast.success(`Seller ${r.data.seller_code || ""} added and selected`);
      setForm(EMPTY);
      onCreated(r.data);
    } catch {
      toast.error("Could not add seller");
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label) => (
    <div><Label>{label}</Label><Input data-testid={`qs-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} /></div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-seller-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Add New Seller</DialogTitle></DialogHeader>
        <DialogDescription className="-mt-2 text-xs text-slate-500">Your machine details are kept. The new seller will be selected automatically.</DialogDescription>
        <div className="space-y-3">
          {field("name", "Seller Name *")}
          {field("company_name", "Company Name")}
          <div>
            <Label>Seller Type</Label>
            <Select value={form.seller_type} onValueChange={(v) => set("seller_type", v)}>
              <SelectTrigger data-testid="qs-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SELLER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">{field("mobile", "Mobile *")}{field("whatsapp", "WhatsApp *")}</div>
          <div className="grid grid-cols-2 gap-3">{field("city", "City")}{field("state", "State")}</div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" data-testid="qs-cancel" onClick={onClose} className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700">Cancel</button>
          <button type="button" data-testid="qs-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2 text-sm font-bold text-white disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />} Save & Select
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
